import { between, minValue, numeric, required, requiredUnless } from 'vuelidate/lib/validators';
import { getCsmQuotaAttainmentEditFieldLabel } from './csmRoleUtils';
import { getAeQuotaAttainmentEditFieldLabel } from './aeRoleUtils';
import { formatNumber } from '@/components/global/mixins';
import {
  jobsDealSizes,
  jobsLeadSources,
  jobsSalesCycles,
  salesMethodologies,
} from '@/components/partials/jobs/constants/selectableOptions';
import { QUOTA_ATTAINMENT_LAST_PERIOD_LABEL } from '@/components/partials/jobs/JobsProfile/constants';
import { getCurrencyData } from '@/components/partials/jobs/utils';
import type {
  FormValidations,
  GenerateFormFunction,
  GetValidationPayload,
  GetVisibleFieldsFunction,
  InquiryForm,
  InquiryInput,
  JobsFormField,
  JobsFormFieldWithRenderedAttrs,
  JobsSectionModels,
  PrepareFormForUpsertFunction,
  SelectableOption as JobsSelectableOption,
  UserInput,
} from '@/components/partials/public-portfolio/PortfolioJobsInquirySection/PortfolioJobsInquirySectionProfile/types';
import type { Client } from '@/types/graphql';
import { JobsInquiryAeQuota, JobsInquiryPosition } from '@/types/graphql';
import { getOteIsLowerThanBaseSalaryValidation } from '@/components/partials/jobs/constants/inquiryFields';

export interface SelectableOption {
  title: string;
  key: string;
  image?: string;
  returnValue?: string | number | boolean;
  attrs?: object;
  subtitle?: string;
  selected?: boolean;
  imageName?: string;
}

export const getStringOfValues = <Type extends { name: string }>(
  field: Type[] | undefined | null,
): string | undefined => {
  if (!field?.length) return undefined;

  return field.map(element => element?.name).join(', ');
};

export const getSelectedItemsTitle = (selectedKeys: string[], fieldActions: SelectableOption[]): string[] => {
  return (
    selectedKeys?.reduce((acc, selectedKey) => {
      const item = fieldActions.find(action => action.key === selectedKey);

      if (!item) return acc;

      acc.push(item.title);

      return acc;
    }, [] as string[]) || []
  );
};

export const isSingleFieldFilled = <Type>(value: Type): boolean => {
  if (typeof value === 'boolean') return true;

  return Boolean(value);
};

export const isMultiselectFieldHasSelectedOption = <Type>(value: Type[] | undefined | null): boolean => {
  if (!value?.length) return false;

  return Boolean(value.length);
};

export const isMultiselectFieldHasSelectedOptionOrOtherOptionFilled = <Main, Other>(
  mainValue: Main[] | undefined | null,
  otherValue: Other | undefined,
): boolean => {
  if (!mainValue && !otherValue) return false;

  return isMultiselectFieldHasSelectedOption(mainValue) || isSingleFieldFilled(otherValue);
};

export const getQuotaAttainment = (inquiry: InquiryForm) => {
  if (!inquiry?.quotaAttainmentPerPeriod) return undefined;

  return `${inquiry.quotaAttainmentPerPeriod}%`;
};

export const getDealSizeFieldValue = (inquiry: InquiryForm) => {
  if (!inquiry.dealSize) return undefined;

  const dealSize = jobsDealSizes.find(dealSize => dealSize.key === inquiry.dealSize);

  return dealSize?.title;
};

export const getIsClientsFieldHidden = (clients?: Client[] | null) => {
  return Boolean(!clients?.length);
};

export const getBaseSalaryFieldValue = (inquiry: InquiryForm) => {
  if (!inquiry?.baseSalary) return undefined;

  return formatNumber.methods.numberToDelimitedFormatter(String(inquiry.baseSalary));
};

export const getOteFieldValue = (inquiry: InquiryForm) => {
  if (!inquiry?.ote) return undefined;

  return formatNumber.methods.numberToDelimitedFormatter(String(inquiry.ote));
};

export const getIncomeLastYearFieldValue = (inquiry: InquiryForm) => {
  if (!inquiry?.revenue) return undefined;

  return formatNumber.methods.numberToDelimitedFormatter(String(inquiry.revenue));
};

export const getSalesCycleFieldValue = (inquiry: InquiryForm) => {
  const salesCycle = jobsSalesCycles.find(salesCycle => salesCycle.key === inquiry.salesCycleLength);

  return salesCycle?.title;
};

export const getRankOnTeamFieldValue = (inquiry: InquiryForm) => {
  if (!inquiry?.rankOnTeam) return undefined;

  return `#${inquiry.rankOnTeam}`;
};

export const getQuotaHitByTeamFieldValue = (inquiry: InquiryForm) => {
  if (!inquiry?.quotaAttainmentByTeamPerPeriod) return undefined;

  return `${inquiry.quotaAttainmentByTeamPerPeriod}%`;
};

export const getQuotaHitByTeamFieldLabel = (inquiry: InquiryForm) => {
  const baseLabel = '% of sales team hit quota';

  if (inquiry?.positionMain === JobsInquiryPosition.Vp) return `${baseLabel} last period`;

  return baseLabel;
};

export const getAeLeadSourcesFeldValue = (inquiry: InquiryForm) => {
  if (!inquiry?.aeLeadSourcesMain) return undefined;

  const leadSourceTitles = getSelectedItemsTitle(inquiry.aeLeadSourcesMain, jobsLeadSources);

  const result = [...leadSourceTitles, inquiry.aeLeadSourceOther].filter(Boolean);

  if (result.length > 0) {
    return result.join(', ');
  }

  return '';
};

export const getSalesMethodologiesFieldValue = (inquiry: InquiryForm) => {
  if (!inquiry.salesMethodologies) return '';

  const salesMethodologyTitles = getSelectedItemsTitle(inquiry.salesMethodologies, salesMethodologies);

  if (!salesMethodologyTitles.length) return '';

  return salesMethodologyTitles.join(', ');
};

export const getVisibleFields: GetVisibleFieldsFunction = ({ fields, form }) => {
  return fields.reduce((acc: JobsFormFieldWithRenderedAttrs[], field) => {
    if (field.getIsHidden && field.getIsHidden(form)) {
      return acc;
    }

    acc.push({
      ...field,
      attrs: field.getAttrs(form),
    });
    return acc;
  }, []);
};

export const getFieldsToUpsert = (fields: JobsFormField[]): string[] => {
  return fields.reduce((acc: string[], field) => {
    if (
      field.type === 'JobsMultiselectWithCustomOption' ||
      (field.type === 'JobsSelectWithCustomOption' && field.attributeNameOther)
    ) {
      acc.push(field.attributeNameOther);
    }

    if (!field.attributeName) return acc;

    acc.push(field.attributeName);

    return acc;
  }, []);
};

export const generateForm: GenerateFormFunction = ({ inquiry }) => {
  return { ...inquiry };
};

export const generateFormForQuotaAndDealsWithClients: GenerateFormFunction = ({ inquiry, user }) => {
  const solidClients = (user?.solidClients || []).map(({ company }) => company.id);
  return { ...inquiry, solidClients };
};

export const prepareFormForUpsert: PrepareFormForUpsertFunction = ({ fields, form }) => {
  const fieldsToUpsert = getFieldsToUpsert(fields);
  const inquiryAttrs = fieldsToUpsert.reduce((acc: InquiryInput, formFieldKey) => {
    const field = fields.find(field => field.attributeName === formFieldKey);

    if (field?.type === 'BravadoTextInputV3' && field?.upsertAsInt) {
      // REFACTOR ME
      // @ts-ignore
      acc[formFieldKey] = parseInt(form[formFieldKey], 10);
      return acc;
    }

    if (field?.attributeName === 'solidClients') return acc;

    // REFACTOR ME
    // @ts-ignore
    acc[formFieldKey] = form[formFieldKey];
    return acc;
  }, {});
  return { inquiryAttrs };
};

export const prepareFormForUpsertForQuotaAndDealsModal: PrepareFormForUpsertFunction = ({ fields, form }) => {
  const { inquiryAttrs } = prepareFormForUpsert({
    fields,
    form,
  });

  const userAttrs: UserInput | undefined = form.solidClients
    ? {
        unarchivedClientCompanyIds: form.solidClients,
      }
    : undefined;
  return { inquiryAttrs, userAttrs };
};

export const getOteValidation = (form: InquiryForm) => {
  return {
    numeric,
    required,
    oteIsLowerThanBaseSalary: getOteIsLowerThanBaseSalaryValidation({ form, required: true }),
  };
};

export const getQuotaAttainmentByTeamValidation = () => {
  return { numeric, between: between(0, 100) };
};

export const validateEditSalarySectionModalForm = ({
  form,
  hasAdminPrivilege,
}: GetValidationPayload): FormValidations => {
  return {
    baseSalary: { numeric, greaterThanZero: hasAdminPrivilege ? () => true : minValue(1) },
    ote: {
      numeric,
      oteIsLowerThanBaseSalary: getOteIsLowerThanBaseSalaryValidation({ form, required: true }),
    },
    revenue: { numeric, greaterThanZero: hasAdminPrivilege ? () => true : minValue(1) },
    salesCycleLength: {
      required: requiredUnless(() => hasAdminPrivilege),
    },
    rankOnTeam: { numeric },
    quotaAttainmentByTeamPerPeriod: getQuotaAttainmentByTeamValidation(),
    teamSize: { numeric },
  };
};

export const mapOnboardingOptionsToSelectInput = (onboardingOption: SelectableOption): JobsSelectableOption => ({
  value: onboardingOption.key,
  label: onboardingOption.title,
});

export const getDesiredCriteriaSectionFullfillnes = ({ inquiry, user }: JobsSectionModels) => {
  return [
    isSingleFieldFilled(inquiry.positionMain),
    isMultiselectFieldHasSelectedOption(inquiry.desiredCompanySeriesStages),
    isMultiselectFieldHasSelectedOptionOrOtherOptionFilled(inquiry.dealBreakersMain, inquiry.dealBreakerOther),
    isSingleFieldFilled(user.city),
    isSingleFieldFilled(inquiry.willingToRelocate),
    isSingleFieldFilled(inquiry.idealJobDescription),
  ];
};

export const mapJobsOnboardingSelectOptionsToBravadoSelectOptions = (
  selectableItems: SelectableOption[],
): JobsSelectableOption[] => {
  return selectableItems.map(({ title, returnValue }) => ({ value: returnValue as string, label: title }));
};

export const getQuotaAttainmentEditFieldLabel = (inquiry: InquiryForm) => {
  if (inquiry.positionMain === JobsInquiryPosition.AccountExecutive) return getAeQuotaAttainmentEditFieldLabel(inquiry);
  if (inquiry.positionMain === JobsInquiryPosition.Vp) return getAeQuotaAttainmentEditFieldLabel(inquiry);
  if (inquiry.positionMain === JobsInquiryPosition.Csm) return getCsmQuotaAttainmentEditFieldLabel(inquiry);

  // For SDR and Other positions
  return QUOTA_ATTAINMENT_LAST_PERIOD_LABEL;
};

export const getQuotaDependentFieldLabel = ({
  periodKey,
  baseLabel,
  periodPrefix,
  postfix = '',
}: Record<string, string>) => {
  const getFullString = (periodName: string) => `${baseLabel} ${periodPrefix} ${periodName}${postfix}`;

  switch (periodKey) {
    case JobsInquiryAeQuota.Monthly:
      return getFullString('month');
    case JobsInquiryAeQuota.Quarterly:
      return getFullString('quarter');
    case JobsInquiryAeQuota.Annual:
      return getFullString('year');
    default:
      return baseLabel;
  }
};

export const getQuotaFieldLabel = (inquiry: InquiryForm) => {
  const { labelTitle } = getCurrencyData(inquiry.currency);
  return `What is your quota amount in ${labelTitle}?`;
};

export const getOteFieldLabel = (inquiry: InquiryForm) => {
  const { labelTitle } = getCurrencyData(inquiry.currency);
  return `What is your current OTE in ${labelTitle}? (OTE = Base + Variable)`;
};

export const getBaseSalaryFieldLabel = (inquiry: InquiryForm) => {
  const { labelTitle } = getCurrencyData(inquiry.currency);
  return `What's your current base salary in ${labelTitle}?`;
};

export const getRevenueFormLabel = (inquiry: InquiryForm) => {
  const { labelTitle } = getCurrencyData(inquiry.currency);
  return `How much money did you make last year in ${labelTitle}?`;
};
